/**
 * @file        src/pages/wallet/WalletDetail.jsx
 * @description One user's wallet: balance, status controls (freeze/unfreeze/
 *              close), credit buckets with expiry, and the full ledger. Add money
 *              and refund open the password-confirmed action modal.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-07-23 Claude — Initial (Admin Wallet Management).
 *   - 2026-07-24 Claude — Freeze/reactivate/close confirm now labels its button
 *                          with the actual verb (was the shared "Delete" default)
 *                          and only Close gets the red danger styling.
 *   - 2026-08-04 Claude — Agent retirement (CR E8): "Agency Owner"/"Agency User"
 *                          role labels reworded to "Corporate Owner"/
 *                          "Corporate User".
 *   - 2026-08-10 Claude — A B2B user has no personal wallet — the server now
 *                          resolves this page to their company's POOLED wallet
 *                          (wallet.pooled/companyName) instead of a personal
 *                          one. Shows a banner and warns before freeze/close,
 *                          since those actions now affect every employee of
 *                          that company, not just this one.
 *   - 2026-09-12 Claude — This page is RETAIL-only. The server no longer
 *                          resolves a corporate employee here at all — it
 *                          refuses with 409 COMPANY_WALLET_ONLY, because a
 *                          corporate account has exactly one wallet and the
 *                          company owns it. So the pooled banner and the
 *                          pooled freeze/close warning are gone (they can no
 *                          longer render) and the refusal is shown instead,
 *                          with a link to Company Wallets for a stale link or
 *                          bookmark.
 *   - 2026-09-24 Claude — Its private wallet-type map moved to
 *                          lib/walletTypes, which the company wallet ledger and
 *                          the Reports Wallet tab (both of which printed the raw
 *                          enum) now share.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { toast } from '../../lib/toast';
import { walletTypeLabel } from '../../lib/walletTypes';
import { confirm } from '../../lib/confirm';
import { qk } from '../../api/queryKeys';
import { getWalletDetail, getWalletTransactions, setWalletStatus } from '../../api/wallets';
import WalletActionModal from './WalletActionModal';
import SettleDueModal from './SettleDueModal';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_TONE = { active: 'ok', frozen: 'warn', closed: 'rej' };
// Retail only reaches this page; the map stays a map so an unexpected role
// shows as its raw value rather than a blank cell.
const ROLE_LABEL = { b2c_user: 'Customer' };

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateTime(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function WalletDetail() {
  const { userId } = useParams();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [modal, setModal] = useState(null); // 'credit' | 'refund' | 'due' | null

  const { data: detail, isLoading, error } = useQuery({
    queryKey: qk.wallet.detail(userId),
    queryFn: () => getWalletDetail(userId),
    retry: false,
  });

  const txParams = { page, pageSize };
  const { data: txData, isLoading: txLoading } = useQuery({
    queryKey: qk.wallet.transactions(userId, txParams),
    queryFn: () => getWalletTransactions(userId, txParams),
    placeholderData: keepPreviousData,
  });

  const statusMut = useMutation({
    mutationFn: (action) => setWalletStatus(userId, action),
    onSuccess: (_res, action) => {
      toast.ok('Updated', `Wallet ${action === 'unfreeze' ? 'reactivated' : action + 'd'}.`);
      qc.invalidateQueries({ queryKey: ['wallet'] });
    },
    onError: (e) => toast.err('Failed', e?.response?.data?.error?.message || 'Could not update the wallet.'),
  });

  async function changeStatus(action, verb) {
    // The shared confirm() defaults its button to a red "Delete"; freeze /
    // reactivate / close are not deletes, so label the button with the actual
    // verb and reserve the danger (red) styling for the terminal Close action.
    if (!(await confirm({ message: `${verb} this wallet?`, confirmLabel: verb, danger: action === 'close' }))) return;
    statusMut.mutate(action);
  }

  const d = detail?.data;
  const u = d?.user;
  const w = d?.wallet;
  const buckets = d?.buckets || [];
  const tx = txData?.data || [];
  const txTotal = txData?.meta?.total || 0;

  return (
    <>
      <PageHead
        title="Wallet"
        subtitle={<Link to="/account/wallets" className="muted">← Back to Wallet Management</Link>}
      />

      {error ? (
        <article className="panel glass">
          <p>{error?.response?.data?.error?.message || 'This wallet could not be opened.'}</p>
          {error?.response?.data?.error?.code === 'COMPANY_WALLET_ONLY' ? (
            <p className="muted">
              <Link to="/account/company-wallets">Go to Company Wallets →</Link>
            </p>
          ) : null}
        </article>
      ) : isLoading || !d ? <article className="panel glass"><TableSkeleton /></article> : (
        <>
          <article className="panel glass" style={{ marginBottom: 16 }}>
            <div className="wallet-head" style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: '0 0 4px' }}>{`${u.firstName} ${u.lastName}`.trim() || u.email}</h3>
                <p className="muted" style={{ margin: 0 }}>
                  {u.email} · {ROLE_LABEL[u.role] || u.role}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="muted" style={{ fontSize: '.8em' }}>Spendable</div>
                <div style={{ fontSize: '1.8em', fontWeight: 700 }}>{inr(w.balance)}</div>
                {/* Three figures, never netted into one: what can be SPENT, what
                    is OWED, and the NET the customer sees on their own wallet —
                    the only one that can be negative. Showing only a balance
                    beside a customer who is looking at -Rs.2,000 is how support
                    and the customer end up describing different accounts. */}
                {Number(w.dues) > 0 ? (
                  <div style={{ marginTop: 6 }}>
                    <div className="muted" style={{ fontSize: '.8em' }}>Outstanding</div>
                    <div style={{ fontSize: '1.1em', fontWeight: 700, color: 'var(--rose)' }}>{inr(w.dues)}</div>
                    <div className="muted" style={{ fontSize: '.8em', marginTop: 4 }}>
                      Net <b style={{ color: Number(w.net) < 0 ? 'var(--rose)' : undefined }}>{inr(w.net)}</b>
                      {w.duesSince ? <> · since {fmtDate(w.duesSince)}</> : null}
                    </div>
                  </div>
                ) : null}
                <Chip tone={STATUS_TONE[w.status] || 'pend'}>{w.status}</Chip>
              </div>
            </div>

            {d.expiringSoon ? (
              <p className="muted" style={{ marginTop: 12 }}>
                <Icon name="clock" /> {inr(d.expiringSoon.amount)} expiring on {fmtDate(d.expiringSoon.nextExpiryAt)}
              </p>
            ) : null}

            <div className="form-actions" style={{ marginTop: 16, justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn primary" onClick={() => setModal('credit')} disabled={w.status === 'closed'}>
                <Icon name="plus" /> Add money
              </button>
              <button className="btn ghost" onClick={() => setModal('refund')} disabled={w.status === 'closed'}>
                Refund to wallet
              </button>
              {/* Settling is deliberately NOT disabled on a frozen or closed
                  wallet: a customer must always be able to pay us back, and
                  freezing stops spending, not repayment. */}
              {Number(w.dues) > 0 ? (
                <button className="btn ghost" onClick={() => setModal('due')}>
                  Record payment against due
                </button>
              ) : null}
              {w.status === 'active' && (
                <button className="btn ghost" onClick={() => changeStatus('freeze', 'Freeze')}>Freeze wallet</button>
              )}
              {w.status === 'frozen' && (
                <button className="btn ghost" onClick={() => changeStatus('unfreeze', 'Reactivate')}>Reactivate wallet</button>
              )}
              {w.status !== 'closed' && (
                <button className="btn danger" onClick={() => changeStatus('close', 'Close')}>Close wallet</button>
              )}
            </div>
          </article>

          <article className="panel glass" style={{ marginBottom: 16 }}>
            <h4 style={{ marginTop: 0 }}>Live credit buckets</h4>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Source</th><th>Original</th><th>Remaining</th><th>Expires</th><th>Repayable</th><th>Note</th></tr></thead>
                <tbody>
                  {buckets.length === 0 ? (
                    <tr><td colSpan={6} className="empty">No live credit — balance is zero.</td></tr>
                  ) : buckets.map((b) => (
                    <tr key={b.id}>
                      <td>{walletTypeLabel(b.source)}</td>
                      <td className="mono">{inr(b.originalAmount)}</td>
                      <td className="mono"><b>{inr(b.remaining)}</b></td>
                      <td>{b.expiresAt ? fmtDate(b.expiresAt) : <span className="muted">Never</span>}</td>
                      {/* Spending a repayable bucket creates a due. Listed per
                          bucket because "Rs.30,000 available" reads very
                          differently when Rs.20,000 of it has to be paid back. */}
                      <td>
                        {b.repayable
                          ? <Chip tone="pend">Credit — repayable</Chip>
                          : <span className="muted">Theirs</span>}
                      </td>
                      <td>{b.note || <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <article className="panel glass">
            <h4 style={{ marginTop: 0 }}>Transaction history</h4>
            <div className="table-wrap">
              {txLoading ? <TableSkeleton /> : (
                <table className="table">
                  <thead><tr><th>Date</th><th>Type</th><th>Reference</th><th>Amount</th><th>Balance</th><th>Note</th></tr></thead>
                  <tbody>
                    {tx.length === 0 ? (
                      <tr><td colSpan={6} className="empty">No transactions yet.</td></tr>
                    ) : tx.map((t) => (
                      <tr key={t.id}>
                        <td>{fmtDateTime(t.createdAt)}</td>
                        <td>{walletTypeLabel(t.type)}</td>
                        <td className="mono">{t.reference || '—'}</td>
                        <td className="mono" style={{ color: t.direction === 'credit' ? 'var(--ok, #128a3a)' : 'var(--rej, #c0392b)' }}>
                          {t.direction === 'credit' ? '+' : '−'}{inr(t.amount)}
                        </td>
                        <td className="mono">{inr(t.balanceAfter)}</td>
                        <td>{t.note || <span className="muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <Pager page={page} pageSize={pageSize} total={txTotal} onPage={setPage} />
          </article>

          {modal ? (
            modal === 'due' ? (
              <SettleDueModal
                row={{
                  ownerType: 'user',
                  ownerId: u.id,
                  ownerName: `${u.firstName} ${u.lastName}`.trim() || u.email,
                  dues: w.dues,
                }}
                onClose={() => setModal(null)}
              />
            ) : <WalletActionModal mode={modal} user={u} onClose={() => setModal(null)} />
          ) : null}
        </>
      )}
    </>
  );
}
